import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SignIn } from "@clerk/clerk-react";

import { PATH_ROOT } from "@/routes/paths";
import { isNonProdEnvironment } from "src/constants/api.constants";
import EnvironmentSwitcher from "src/components/EnvironmentSwitcher";
import { useIsDemo, setDemoMode } from "@/hooks/common/useIsDemo";

import { LeftPanelCopy } from "./components/LeftPanelCopy";
import { WelcomeStageLayout } from "./components/WelcomeStageLayout";
import { WELCOME_TOTAL_STEPS } from "./constants";
import { INITIAL_DEMO_WELCOME_STATE, type DemoWelcomeState } from "./types";
import {
  StepAbout,
  StepConnect,
  StepInvite,
  StepPopulate,
  StepSignIn,
  StepWorkspace,
} from "./welcomeSteps";

export default function AriDemoWelcomePage() {
  const navigate = useNavigate();
  const demo = useIsDemo();
  const [step, setStep] = useState(0);
  const [state, setState] = useState<DemoWelcomeState>(INITIAL_DEMO_WELCOME_STATE);
  const [showEnvSwitcher, setShowEnvSwitcher] = useState(false);

  const next = () => setStep((s) => Math.min(s + 1, WELCOME_TOTAL_STEPS - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  if (!demo) {
    return (
      <div className="fixed inset-0 bg-[#181818] flex items-center justify-center">
        <SignIn
          routing="path"
          path="/sign-in"
          signUpUrl="/auth/register"
          fallbackRedirectUrl="/organizations"
        />

        {isNonProdEnvironment() && (
          <div className="fixed top-4 right-4 z-50 flex gap-2">
            <button
              onClick={() => setDemoMode(true)}
              className="rounded-lg bg-white/10 px-3 py-1.5 text-xs text-white/70 backdrop-blur hover:bg-white/20 transition"
            >
              Try Demo
            </button>
            <button
              onClick={() => setShowEnvSwitcher(true)}
              className="rounded-lg bg-white/10 px-3 py-1.5 text-xs text-white/70 backdrop-blur hover:bg-white/20 transition"
            >
              Environment
            </button>
          </div>
        )}

        <EnvironmentSwitcher
          open={showEnvSwitcher}
          onClose={() => setShowEnvSwitcher(false)}
        />
      </div>
    );
  }

  const stepContent = [
    <StepSignIn key="sign-in" state={state} setState={setState} next={next} demo={demo} />,
    <StepAbout key="about" state={state} setState={setState} next={next} back={back} />,
    <StepWorkspace key="workspace" state={state} setState={setState} next={next} back={back} />,
    <StepInvite
      key="invite"
      state={state}
      setState={setState}
      next={next}
      back={back}
      isOwner={state.mode === "create"}
    />,
    <StepConnect key="connect" state={state} setState={setState} next={next} back={back} />,
    <StepPopulate key="populate" state={state} onLaunch={() => navigate(PATH_ROOT.demo.inbox)} />,
  ][step];

  return (
    <>
      <WelcomeStageLayout step={step} leftPanel={<LeftPanelCopy step={step} />}>
        {stepContent}
      </WelcomeStageLayout>

      {isNonProdEnvironment() && (
        <div className="fixed top-4 right-4 z-50 flex gap-2">
          <button
            onClick={() => setDemoMode(false)}
            className="rounded-lg bg-white/10 px-3 py-1.5 text-xs text-white/70 backdrop-blur hover:bg-white/20 transition"
          >
            Exit Demo
          </button>
          <button
            onClick={() => setShowEnvSwitcher(true)}
            className="rounded-lg bg-white/10 px-3 py-1.5 text-xs text-white/70 backdrop-blur hover:bg-white/20 transition"
          >
            Environment
          </button>
        </div>
      )}

      <EnvironmentSwitcher
        open={showEnvSwitcher}
        onClose={() => setShowEnvSwitcher(false)}
      />
    </>
  );
}
